/* import __COLOCATED_TEMPLATE__ from './geometry.hbs'; */
import { set, computed } from '@ember/object';
import Component from '@ember/component';
import { pie, arc } from 'd3-shape';
import { assert } from '@ember/debug';
import cssNumericalProperty from '@square/ember-prism/utils/css-numerical-property';
export default Component.extend({
  tagName: 'g',
  attributeBindings: ['transform', 'mask'],
  mask: null,
  transform: computed('_transform', 'outerRadius', {
    get() {
      if (this._transform) {
        return this._transform;
      }
      return `translate(${this.outerRadius} ${this.outerRadius})`;
    },
    set(_, value) {
      set(this, '_transform', value);
      return value;
    }
  }),
  segments: null,
  /**
   * Width of the segments in pixels.
   *
   * @computed thickness
   * @type {Number}
   */
  thickness: cssNumericalProperty('--thickness'),
  /**
   * How rounded the segments are, in pixels.
   *
   * @computed cornerRadius
   * @type {Number}
   */
  cornerRadius: cssNumericalProperty('--corner-radius'),
  /**
   * Space between segments in radians.
   *
   * @computed padAngle
   * @type {Number}
   */
  padAngle: cssNumericalProperty('--pad-angle'),
  /**
   * Radius of the outer edge of the segments.
   *
   * @computed outerRadius
   * @type {Number}
   */
  outerRadius: computed('diameter', function () {
    return this.diameter / 2;
  }),
  /**
   * Radius of the inner edge of the segments.
   *
   * @computed innerRadius
   * @type {Number}
   */
  innerRadius: computed('outerRadius', 'thickness', function () {
    return this.outerRadius - this.thickness;
  }),
  /**
   * Array of SVG path geometry strings to be assiged to the `d` attribute.
   *
   * @computed arcs
   * @type {Array<String>}
   */
  arcs: computed('cornerRadius', 'innerRadius', 'outerRadius', 'padAngle', 'segments.[]', 'useMask', function () {
    assert('Prism Macaroni Chart geometry requires at least one segment!', this.segments && this.segments.length >= 1);
    const data = this.segments.map(({
      value
    }) => value);
    return pie().sort(null).padAngle(this.useMask ? 0 : this.padAngle)(data).map(arc().outerRadius((_d, index) => {
      // attempt to use custom segment widths if available
      return this.segments[index].outerRadius || this.outerRadius;
    }).innerRadius((_d, index) => {
      // attempt to use custom segment widths if available
      return this.segments[index].innerRadius || this.innerRadius;
    }).cornerRadius(this.useMask ? 0 : this.cornerRadius));
  }),
  /**
   * Data to be rendered into the DOM as SVG `path` elements.
   *
   * @computed paths
   * @type {Object} with `index`, `d` (from arcs), and `className` (from segments)
   */
  paths: computed('arcs', 'segments', function () {
    return this.arcs.map((d, i) => {
      const segment = this.segments[i];
      const className = segment.className || 'prism-macaroni-chart--default';
      const value = segment.value;
      // TODO: remove index and partialIndex when removing `classes`
      const index = segment.index || i;
      const partialIndex = segment.partialIndex;
      return {
        index,
        d,
        className,
        partialIndex,
        value
      };
    });
  }),
  /**
   * An action handler that is called by the component when the user hovers
   * over the path corresponding to the given segment. Receives
   * `index, partialIndex` on hover and `-1, -1` when the mouse exits that
   *  segment. Touches also work.
   *
   * @argument onGeometryHover
   * @type {Action}
   */
  onGeometryHover() {},
  gte313: computed(function () {
    // https://deprecations.emberjs.com/v3.x/#toc_action-mouseenter-leave-move
    // In 3.13 the mouseEnter and mouseLeave events were deprecated for actions
    // so this boolean is used to switch to the {{on}} helper instead.
    return true;
  })
});